import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Loader2, User } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

export const AgendaTeam = ({ selectedDate }) => {
  const [agenda, setAgenda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState({});

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/booking/team-agenda?data=${dateStr}`);
      setAgenda(r.data);
    } catch {}
    finally { setLoading(false); }
  }, [dateStr]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={22} /></div>;
  if (!agenda) return null;

  const team = agenda.team.filter(m => m.slots.length > 0 || m.fuori_orario.length > 0);
  const visible = team.filter(m => !hidden[m.user_id]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="agenda-team">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="text-sm font-bold text-slate-800 capitalize">
          Agenda Team — {format(selectedDate, 'EEEE dd MMMM', { locale: it })}
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {team.map(m => (
            <button key={m.user_id} onClick={() => setHidden(h => ({ ...h, [m.user_id]: !h[m.user_id] }))}
              className={`text-[10px] px-2 py-1 rounded-full font-medium transition-colors ${hidden[m.user_id] ? 'bg-slate-100 text-slate-400 line-through' : 'bg-slate-800 text-white'}`}
              data-testid={`team-filter-${m.user_id}`}>
              {m.nome.split(' ')[0]}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-center text-sm text-slate-400 py-10">Nessun membro del team con orari per questo giorno</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="flex gap-3 min-w-fit">
            {visible.map(m => (
              <div key={m.user_id} className="min-w-[180px] flex-1" data-testid={`team-col-${m.user_id}`}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 text-[10px] font-bold">
                    {m.nome.split(' ').map(w => w[0]).join('').slice(0, 2)}
                  </div>
                  <p className="text-xs font-semibold text-slate-800 truncate">{m.nome}</p>
                </div>
                <div className="space-y-1">
                  {m.slots.length === 0 && <p className="text-[10px] text-slate-300 text-center py-4">Non disponibile</p>}
                  {m.slots.map(s => s.libero ? (
                    <div key={s.ora} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
                      <span className="text-[10px] font-mono font-semibold text-emerald-700">{s.ora}</span>
                      <span className="text-[10px] text-emerald-600">Libero</span>
                    </div>
                  ) : (
                    <div key={s.ora} className="px-2 py-1.5 rounded-lg bg-white border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono font-bold text-slate-700">{s.ora}</span>
                        <User size={9} className="text-slate-400" />
                        <span className="text-[10px] font-medium text-slate-700 truncate">{s.nome_cliente || s.titolo || 'Occupato'}</span>
                      </div>
                    </div>
                  ))}
                  {m.fuori_orario.map((s, i) => (
                    <div key={`fo-${i}`} className="px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-100">
                      <span className="text-[10px] font-mono font-bold text-amber-700">{s.ora}</span>
                      <span className="text-[10px] text-amber-700 ml-1.5 truncate">{s.nome_cliente || s.titolo}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
